// src/pages/Assistant.js
import React, { useState, useEffect, useRef, use } from "react";

import { useTheme } from "../context/ThemeContext";
import { borderRadius, spacing } from "../utils/tokens";
import Typography from "./Typography";

import BlurText from "./BlurText";
import DecryptedText from "./DecryptedText";


function Chat ({ news, assets }){

    const { theme } = useTheme(); 

    const [messages, setMessages] = useState([]);
    const [userInput, setUserInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const [finalAssistantMessage, setFinalAssistantMessage] = useState("");
    const [hasBlurred, setHasBlurred] = useState(false);

    const messagesEndRef = useRef(null);

    // Scroll to bottom of chat when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Function to send user message to the assistant
    const handleSend = async () => {
        if (!userInput.trim()) return;

        // Add the user message to the chat
        const newUserMessage = { role: "user", content: userInput };
        setMessages((prev) => [...prev, newUserMessage]);

        setIsLoading(true);
        setUserInput("");

        // Build the system message that includes instructions about summarizing articles, etc.
        const systemMessage = {
            role: "system",
            content: createSystemPrompt(news, assets),
        };

        // Stream the response from the Chat completion
        try {
            const streamData = await fetchStreamedChatCompletion(
                [systemMessage, ...messages, newUserMessage]
            );

            // Build the response in a chunk-based manner
            let assistantResponse = "";

            for await (const chunk of streamData) {
                if (chunk === "[DONE]") {
                    setFinalAssistantMessage(assistantResponse);
                    setHasBlurred(false);

                    // Comment this out if you want to update assistant message in real-time instead of using the animation
                    setMessages((prev) => [
                      ...prev,
                      { role: "assistant", content: assistantResponse }
                    ]);                

                    break;
                }
                assistantResponse += chunk;

                // Uncomment this if you want to update assistant message in real-time instead of using the animation
                // setMessages((prevMessages) => {
                //     const lastMessage = prevMessages[prevMessages.length - 1];
                //     if (lastMessage && lastMessage.role === "assistant") {
                //         const updatedMessages = [...prevMessages];
                //         updatedMessages[updatedMessages.length - 1] = {
                //           ...lastMessage,
                //           content: assistantResponse,
                //         };
                //         return updatedMessages;
                //       } else {
                //         return [
                //           ...prevMessages,
                //           {
                //             role: "assistant",
                //             content: assistantResponse,
                //           },
                //         ];
                //       }
                // });
            }
        } catch (error) {
            console.log("Error streaming chat completion:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.container}>
          {/* <div style={styles.chatWindow}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  ...styles.messageBubble,
                  alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                  backgroundColor: msg.role === "user" ? "#007aff" : "#e1e1e1",
                  color: msg.role === "user" ? "#ffffff" : "#000000",
                }}
              >
                {msg.content}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div> */}

          <div style={styles.chatWindow}>
            {messages.map((msg, idx) => {
              const isLastMessage = idx === messages.length - 1;
              const isAssistant = msg.role === "assistant";

              if (isAssistant && isLastMessage && finalAssistantMessage) {
                // If we haven't animated yet, show BlurText
                if (!hasBlurred) {
                  return (
                    <div
                      key={idx}
                      style={{
                        ...styles.messageBubble,
                        alignSelf: "flex-start",
                        backgroundColor: "#EFF0FC",
                        color: "#222222",
                      }}
                    >
                      {/* <BlurText
                        text={finalAssistantMessage}
                        delay={20}
                        animateBy="words"
                        direction="top"
                        onAnimationComplete={() => {
                          setHasBlurred(true);
                        }}
                        className="text-2xl mb-8"
                      /> */}
                      <DecryptedText
                        text={finalAssistantMessage}
                        speed={100}
                        maxIterations={20}
                        characters="ABCD1234!?"
                        className="revealed"
                        parentClassName="all-letters"
                        encryptedClassName="encrypted"
                        animateOn="view"
                      />
                    </div>
                  );
                } else {
                  // If the animation has already completed, just show final text
                  return (
                    <div
                      key={idx}
                      style={{
                        ...styles.messageBubble,
                        alignSelf: "flex-start",
                        backgroundColor: "#EFF0FC",
                        color: "#222222",
                      }}
                    >
                      <Typography variant="summary" type="editorial">{finalAssistantMessage}</Typography>
                    </div>
                  );
                }
              } else {
                // All other messages (user messages or older ones)
                return (
                  <div
                    key={idx}
                    style={{
                      ...styles.messageBubble,
                      alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                      backgroundColor: msg.role === "user" ? "#007aff" : "#e1e1e1",
                      color: msg.role === "user" ? "#ffffff" : "#000000",
                    }}
                  >
                    <Typography variant="summary" type="editorial">{msg.content}</Typography>
                  </div>
                );
              }
            })}
            <div ref={messagesEndRef} />
          </div>
    
          {/* Input box */}
          <div style={styles.inputContainer}>
            <input
              style={styles.input}
              placeholder="Type your message..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSend();
              }}
            />
            <button onClick={handleSend} style={styles.sendButton}>
              Send
            </button>
          </div>
    
          {isLoading && <p>Assistant is thinking...</p>}
        </div>
    );
}

export default Chat;

/**
 * A system prompt instructing the model how to handle:
 * - Summaries of articles
 * - Summaries of stocks
 * - Finance term explanations
 * - Return structured data
 */
function createSystemPrompt(news, assets) {
    return `
  You are a finance assistant. You can:
  - Summarize a list of news articles,
  - Summarize performance of stocks given their tickers and data,
  - Explain finance-related terms,
  - Return responses in a structured JSON format when asked.
  
  Here is the data you have available:
  Articles: ${JSON.stringify(news)}
  Stock Data: ${JSON.stringify(assets)}
  
  When summarizing or explaining, do so succinctly, with relevant detail. 
  If asked for a structured output, respond with valid JSON that includes "summary", "analysis", and "recommendation" fields, for example. 
  Otherwise, answer in plain text.
  
  Always keep in mind you are assisting with finance queries.
  `;
}

// A helper to stream the chat completion from OpenAI
export async function fetchStreamedChatCompletion(chatMessages) {
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    const endpoint = "https://api.openai.com/v1/chat/completions";

    // For streaming responses, set `stream: true`
    const payload = {
        model: "gpt-4o-mini",
        messages: chatMessages,
        temperature: 0.7,
        stream: true,
    };

    const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
    throw new Error(`OpenAI API responded with status ${response.status}`);
    }
    
    // Parse the event stream (SSE)
    // Return an async generator that yields chunk text
    return readResponseStream(response);
}

/**
 * readResponseStream: Converts a fetch Response into an async generator
 * that yields text chunks. Each chunk is partial data from the event stream.
 */
async function* readResponseStream(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
  
    let done = false;
  
    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading;
      if (value) {
        // The raw text from the SSE stream
        const chunk = decoder.decode(value, { stream: true });
        // Each SSE data packet is like:
        // data: {"id":"...","object":"chat.completion.chunk","choices":[...]}\n\n
        // or "data: [DONE]\n\n" when finished
  
        // Split on the SSE 'data:' prefix
        const lines = chunk
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.startsWith("data: "));
  
        for (const line of lines) {
          const jsonStr = line.replace("data: ", "");
          if (jsonStr === "[DONE]") {
            yield "[DONE]";
            return; // End the stream
          }
  
          try {
            const parsed = JSON.parse(jsonStr);
            // Extract the text delta
            const token = parsed.choices?.[0]?.delta?.content;
            if (token) {
              yield token;
            }
          } catch (err) {
            console.error("Could not JSON parse stream chunk:", jsonStr, err);
          }
        }
      }
    }
}

/** Temp simple inline styles */
const styles = {
    container: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      maxWidth: "600px",
      margin: "0 auto",
    },
    chatWindow: {
      flex: 1,
      padding: spacing.xxxs,
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
    },
    messageBubble: {
      margin: "4px 0",
      padding: spacing.m,
      borderRadius: "8px",
      maxWidth: "100%",
      whiteSpace: "pre-wrap",
    },
    inputContainer: {
      display: "flex",
      borderTop: "1px solid #ccc",
    },
    input: {
      flex: 1,
      padding: "10px",
      border: "none",
      outline: "none",
    },
    sendButton: {
      backgroundColor: "#007aff",
      color: "#fff",
      border: "none",
      padding: "10px",
      cursor: "pointer",
    },
  };
  
